import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { useLanguage } from '../../contexts/LanguageContext';
import PurchaseTrendsChart from './components/PurchaseTrendsChart';
import CoffeeTypesChart from './components/CoffeeTypesChart';
import SpendingPatternsChart from './components/SpendingPatternsChart';
import SupplierMetricsCards from './components/SupplierMetricsCards';
import GeographicSourcingMap from './components/GeographicSourcingMap';
import SeasonalTrendsHeatmap from './components/SeasonalTrendsHeatmap';
import ROIInsights from './components/ROIInsights';
import { subDays, subMonths, subYears } from 'date-fns';

const BuyerAnalyticsDashboard = () => {
  const { language } = useLanguage();
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const { user, loading, isAuthenticated } = useBuyerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/buyer-login');
    }
  }, [loading, isAuthenticated, navigate]);

  const content = {
    en: {
      title: 'Analytics Dashboard',
      subtitle: 'Comprehensive insights into your purchasing patterns and supplier relationships',
      dateRange: 'Date Range',
      last30Days: 'Last 30 Days',
      lastQuarter: 'Last Quarter',
      lastYear: 'Last Year',
      custom: 'Custom Range',
      startDate: 'Start Date',
      endDate: 'End Date',
      exportCSV: 'Export CSV',
      exportPDF: 'Export PDF',
      backToDashboard: 'Back to Dashboard',
      loading: 'Loading analytics...'
    },
    zh: {
      title: '分析仪表板',
      subtitle: '全面了解您的采购模式和供应商关系',
      dateRange: '日期范围',
      last30Days: '最近30天',
      lastQuarter: '最近一季度',
      lastYear: '最近一年',
      custom: '自定义范围',
      startDate: '开始日期',
      endDate: '结束日期',
      exportCSV: '导出CSV',
      exportPDF: '导出PDF',
      backToDashboard: '返回仪表板',
      loading: '加载分析中...'
    }
  };

  const t = content?.[language];

  const getDateRangeValues = () => {
    const today = new Date();
    switch (dateRange) {
      case '30days':
        return { start: subDays(today, 30), end: today };
      case 'quarter':
        return { start: subMonths(today, 3), end: today };
      case 'year':
        return { start: subYears(today, 1), end: today };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : subDays(today, 30),
          end: customEndDate ? new Date(customEndDate) : today
        };
      default:
        return { start: subDays(today, 30), end: today };
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // CSV export logic would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Exporting CSV...');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // PDF export logic would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Exporting PDF...');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.loading}</p>
        </div>
      </div>
    );
  }

  const { start, end } = getDateRangeValues();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <FullWidthSection padding="" py="py-8">
          <ContentWrapper maxWidth="6xl" padding="px-5">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/buyer-dashboard')}
              >
                {t?.backToDashboard}
              </Button>
            </div>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  {t?.title}
                </h1>
                <p className="text-muted-foreground">{t?.subtitle}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={handleExportCSV}
                  loading={exporting}
                >
                  {t?.exportCSV}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  onClick={handleExportPDF}
                  loading={exporting}
                >
                  {t?.exportPDF}
                </Button>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h3 className="text-sm font-medium text-foreground mb-4">{t?.dateRange}</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setDateRange('30days')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  dateRange === '30days' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t?.last30Days}
              </button>
              <button
                onClick={() => setDateRange('quarter')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  dateRange === 'quarter' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t?.lastQuarter}
              </button>
              <button
                onClick={() => setDateRange('year')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  dateRange === 'year' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t?.lastYear}
              </button>
              <button
                onClick={() => setDateRange('custom')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  dateRange === 'custom' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t?.custom}
              </button>
              {dateRange === 'custom' && (
                <div className="flex gap-3 ml-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t?.startDate}</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e?.target?.value)}
                      className="px-3 py-2 border border-border rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t?.endDate}</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e?.target?.value)}
                      className="px-3 py-2 border border-border rounded-md text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Analytics Sections */}
          <div className="space-y-6">
            {/* Purchase Trends */}
            <PurchaseTrendsChart language={language} startDate={start} endDate={end} />

            {/* Coffee Types & Spending Patterns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CoffeeTypesChart language={language} startDate={start} endDate={end} />
              <SpendingPatternsChart language={language} startDate={start} endDate={end} />
            </div>

            {/* Supplier Metrics */}
            <SupplierMetricsCards language={language} startDate={start} endDate={end} />

            {/* Geographic Sourcing */}
            <GeographicSourcingMap language={language} startDate={start} endDate={end} />

            {/* Seasonal Trends & ROI */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SeasonalTrendsHeatmap language={language} startDate={start} endDate={end} />
              <ROIInsights language={language} startDate={start} endDate={end} />
            </div>
          </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default BuyerAnalyticsDashboard;